package co.maytes.standupbot.dashboard.pages.app.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.widgets.IconButton
import co.maytes.standupbot.dashboard.components.widgets.TextInputStyle
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.newstandup.NewStandupContainer
import co.maytes.standupbot.dashboard.features.newstandup.NewStandupContract
import co.maytes.standupbot.dashboard.features.roster.ChannelRosterSection
import co.maytes.standupbot.dashboard.features.roster.ChannelRosterStore
import co.maytes.standupbot.dashboard.features.roster.LinearUsersStore
import co.maytes.standupbot.dashboard.features.roster.RosterPickerState
import co.maytes.standupbot.dashboard.features.roster.TextAction
import co.maytes.standupbot.dashboard.features.roster.rememberChannelRosterStore
import co.maytes.standupbot.dashboard.features.roster.rememberLinearUsersStore
import co.maytes.standupbot.dashboard.features.standups.ChannelPicker
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.standups.ScheduleBuilder
import co.maytes.standupbot.dashboard.features.standups.SlackDidNotAnswerHint
import co.maytes.standupbot.dashboard.features.standups.SlackUserPickerModal
import co.maytes.standupbot.dashboard.features.standups.TEMPLATE_DEFAULT_KEY
import co.maytes.standupbot.dashboard.features.standups.TemplatePickerGrid
import co.maytes.standupbot.dashboard.features.standups.rememberSlackDirectoryStore
import co.maytes.standupbot.dashboard.features.standups.rememberStandupsStore
import co.maytes.standupbot.dashboard.features.templates.rememberTemplatesStore
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

@Page("/app/standups/new")
@Composable
fun NewTeamPage() = AuthGuard {
    AppShell {
        NewStandupScreen()
    }
}

@Composable
private fun NewStandupScreen() {
    val auth = rememberAuthStore()
    val ctx = rememberPageContext()
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()
    val standupsStore = rememberStandupsStore()
    val directory = rememberSlackDirectoryStore()
    val templates = rememberTemplatesStore()
    val rosterStore = rememberChannelRosterStore()
    val linearUsers = rememberLinearUsersStore()

    val workspaceId = auth.currentUser?.workspace?.id
    val workspaceTz = auth.currentUser?.workspace?.defaultTimezone
    val defaultTz = remember(workspaceTz) {
        workspaceTz?.takeIf { it.isNotBlank() }
            ?: runCatching { js("Intl.DateTimeFormat().resolvedOptions().timeZone") as String }.getOrNull()
            ?: "UTC"
    }

    val container = remember(standupsStore, directory, templates) {
        NewStandupContainer(scope, standupsStore, directory, templates)
    }

    val state by container.subscribe { action ->
        when (action) {
            is NewStandupContract.Action.Navigate -> ctx.router.navigateTo(action.path)
            is NewStandupContract.Action.Toast -> ui.showToast(
                action.message,
                if (action.error) ToastKind.Error else ToastKind.Success,
            )
        }
    }

    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) {
            container.intent(NewStandupContract.Intent.Init(workspaceId, defaultTz))
        }
    }

    NewStandupView(
        state = state,
        directory = directory,
        templates = templates,
        rosterStore = rosterStore,
        linearUsers = linearUsers,
    ) {
        container.intent(it)
    }
}

@Composable
private fun NewStandupView(
    state: NewStandupContract.State,
    directory: co.maytes.standupbot.dashboard.features.standups.SlackDirectoryStore,
    templates: co.maytes.standupbot.dashboard.features.templates.TemplatesStore,
    rosterStore: ChannelRosterStore,
    linearUsers: LinearUsersStore,
    dispatch: (NewStandupContract.Intent) -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("margin", "0 auto") }
            .maxWidth(PAGE_MAX_WIDTH.px),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.md)) {
                Button(
                    label = "← Back to standups",
                    variant = ButtonVariant.Ghost,
                    onClick = { dispatch(NewStandupContract.Intent.Cancel) },
                )
            }
            SpanText(
                text = "Create a standup",
                modifier = Modifier
                    .fontSize(Typography.h1.size)
                    .fontWeight(Typography.h1.weight)
                    .color(Palette.textPrimary)
                    .margin(bottom = Spacing.xs),
            )
            SpanText(
                text = "Each standup has its own schedule, Slack channel, template, and roster.",
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .color(Palette.textSecondary)
                    .margin(bottom = Spacing.lg),
            )

            Card(modifier = Modifier.fillMaxWidth().padding(Spacing.lg)) {
                Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
                    NameAndChannelFields(state = state, directory = directory, dispatch = dispatch)
                    MembersField(
                        state = state,
                        directory = directory,
                        rosterStore = rosterStore,
                        linearUsers = linearUsers,
                        dispatch = dispatch,
                    )
                    ScheduleField(state = state, dispatch = dispatch)
                    TemplateField(state = state, templates = templates, dispatch = dispatch)
                    SubmitRow(
                        state = state,
                        rosterBlocking = rosterStore.state is RosterPickerState.Loading ||
                            rosterStore.state is RosterPickerState.BotNotInChannel,
                        dispatch = dispatch,
                    )
                }
            }
        }
    }

    if (state.pickerOpen) {
        SlackUserPickerModal(
            directory = directory,
            alreadyOnTeam = state.selectedSlackUserIds,
            onConfirm = { adds, removes ->
                dispatch(NewStandupContract.Intent.PickMembers(adds, removes))
            },
            onDismiss = { dispatch(NewStandupContract.Intent.ClosePicker) },
        )
    }
}

@Composable
private fun NameAndChannelFields(
    state: NewStandupContract.State,
    directory: co.maytes.standupbot.dashboard.features.standups.SlackDirectoryStore,
    dispatch: (NewStandupContract.Intent) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
        FieldLabel("Name & channel")
        Input(
            type = InputType.Text,
            attrs = TextInputStyle.toModifier()
                .fillMaxWidth()
                .toAttrs {
                    attr("aria-label", "Standup name")
                    placeholder("e.g. Platform")
                    value(state.name)
                    onInput { dispatch(NewStandupContract.Intent.SetName(it.value)) }
                },
        )
        val selectedChannel = directory.channels.firstOrNull { it.id == state.channelId }
        val channelsScope = rememberCoroutineScope()
        when (directory.channelsState) {
            LoadState.Idle, LoadState.Loading -> SkeletonLine(widthPct = 100, heightPx = 40, radiusPx = 8)
            is LoadState.Error ->
                SlackDidNotAnswerHint(
                    onRetry = {
                        if (state.workspaceId.isNotBlank()) {
                            channelsScope.launch { directory.refreshChannels(state.workspaceId) }
                        }
                    },
                )
            LoadState.Ready ->
                ChannelPicker(
                    workspaceId = null,
                    selectedId = state.channelId.ifBlank { null },
                    selectedName = selectedChannel?.name,
                    onChange = { id, _ -> dispatch(NewStandupContract.Intent.SetChannelId(id)) },
                    ariaLabel = "Slack channel",
                    injectedChannels = directory.channels,
                )
        }
    }
}

@Composable
private fun MembersField(
    state: NewStandupContract.State,
    directory: co.maytes.standupbot.dashboard.features.standups.SlackDirectoryStore,
    rosterStore: ChannelRosterStore,
    linearUsers: LinearUsersStore,
    dispatch: (NewStandupContract.Intent) -> Unit,
) {
    val channelName = directory.channels.firstOrNull { it.id == state.channelId }?.name
    val channelMemberIds = (rosterStore.state as? RosterPickerState.Ready)?.members.orEmpty().map { it.slackUserId }.toSet()
    val outsideChannel = state.selectedSlackUserIds - channelMemberIds
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
        FieldLabel("Members")
        ChannelRosterSection(
            store = rosterStore,
            workspaceId = state.workspaceId,
            channelId = state.channelId.ifBlank { null },
            channelName = channelName,
            selected = state.selectedSlackUserIds,
            onSelectionChange = { dispatch(NewStandupContract.Intent.SetMembers(it)) },
            linearUsers = linearUsers,
        )
        if (outsideChannel.isNotEmpty()) {
            Row(modifier = Modifier.flexWrap(FlexWrap.Wrap).gap(Spacing.xs)) {
                outsideChannel.forEach { slackId ->
                    val user = directory.users.firstOrNull { it.slackUserId == slackId }
                    val label = user?.displayName ?: "@$slackId"
                    MemberChip(
                        label = label,
                        onRemove = { dispatch(NewStandupContract.Intent.RemoveMember(slackId)) },
                    )
                }
            }
        }
        Row(modifier = Modifier.fillMaxWidth()) {
            TextAction(label = "Add someone who isn't in the channel") {
                dispatch(NewStandupContract.Intent.OpenPicker)
            }
        }
    }
}

@Composable
private fun ScheduleField(
    state: NewStandupContract.State,
    dispatch: (NewStandupContract.Intent) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
        FieldLabel("Schedule")
        ScheduleBuilder(
            value = state.schedule,
            onChange = { dispatch(NewStandupContract.Intent.SetSchedule(it)) },
        )
    }
}

@Composable
private fun TemplateField(
    state: NewStandupContract.State,
    templates: co.maytes.standupbot.dashboard.features.templates.TemplatesStore,
    dispatch: (NewStandupContract.Intent) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
        FieldLabel("Template")
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .role("radiogroup")
                .ariaLabel("Template"),
        ) {
            TemplatePickerGrid(
                templates = templates.list,
                selectedKey = state.selectedTemplateId ?: TEMPLATE_DEFAULT_KEY,
                onSelect = { key ->
                    dispatch(
                        NewStandupContract.Intent.SetTemplateId(
                            key.takeUnless { it == TEMPLATE_DEFAULT_KEY },
                        ),
                    )
                },
            )
        }
    }
}

@Composable
private fun SubmitRow(
    state: NewStandupContract.State,
    rosterBlocking: Boolean,
    dispatch: (NewStandupContract.Intent) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().margin(top = Spacing.xs).gap(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Button(
            label = if (state.creating) "Creating…" else "Create standup",
            variant = ButtonVariant.Primary,
            enabled = state.canSubmit && !rosterBlocking,
            onClick = { dispatch(NewStandupContract.Intent.Submit) },
        )
        Button(
            label = "Cancel",
            variant = ButtonVariant.Ghost,
            enabled = !state.creating,
            onClick = { dispatch(NewStandupContract.Intent.Cancel) },
        )
    }
}

@Composable
private fun FieldLabel(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .fontWeight(Typography.caption.weight)
            .color(Palette.textSecondary),
    )
}

@Composable
private fun MemberChip(label: String, onRemove: () -> Unit) {
    Row(
        modifier = Modifier
            .padding(topBottom = Spacing.xxs, leftRight = Spacing.xs)
            .backgroundColor(Palette.surfaceSubtle)
            .borderRadius(Radius.full)
            .styleModifier { property("border", "1px solid ${Palette.border.cssRgb()}") },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.xxs),
    ) {
        SpanText(text = label, modifier = Modifier.fontSize(Typography.bodySmall.size))
        IconButton(
            ariaLabel = "Remove $label",
            modifier = Modifier.color(Palette.textTertiary),
            onClick = { onRemove() },
        ) {
            SpanText(
                "×",
                modifier = Modifier.fontSize(Typography.body.size).ariaHidden(),
            )
        }
    }
}

private const val PAGE_MAX_WIDTH = 800
